// src/pages/organizations/OrganizationDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Users,
  FolderOpen,
  Edit,
  Save,
  X,
  Loader2,
  MapPin,
  Mail,
  Phone,
  User,
  BarChart3,
  FileText,
  Shield,
  Crown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organization.service';
import { getUsers } from '../../services/user.service';
import { projectService } from '../../services/project.service';
import {
  Organization,
  OrganizationStats,
  UpdateOrganizationDto,
} from '../../types/organization.types';
import { User as UserType } from '../../types/user.types';
import { Project } from '../../types/project.types';
import toast from 'react-hot-toast';

const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<UpdateOrganizationDto>({});
  const [isChangingPrimaryAdmin, setIsChangingPrimaryAdmin] = useState(false);

  const isProviderAdmin = userProfile?.role === 'provider_admin';

  // Load organization data
  useEffect(() => {
    const loadData = async () => {
      if (!id || !isProviderAdmin) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Load organization
        const org = await organizationService.get(id);
        if (!org) {
          toast.error('Organization not found');
          navigate('/organizations');
          return;
        }
        setOrganization(org);
        setEditData({
          name: org.name,
          contactName: org.contactName,
          contactEmail: org.contactEmail,
          contactPhone: org.contactPhone,
          accountingEmails: org.accountingEmails,
          address: org.address,
        });

        // Load stats
        const orgStats = await organizationService.getStats(id);
        setStats(orgStats);

        // Load users in this organization
        const allUsers = await getUsers({ organizationId: id });
        setUsers(allUsers.filter(u => u.organizationId === id));

        // Load projects in this organization
        const orgProjects = await projectService.getOrganizationProjects(id);
        setProjects(orgProjects);
      } catch (error) {
        console.error('Error loading organization:', error);
        toast.error('Failed to load organization');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, isProviderAdmin, navigate]);

  // Handle save
  const handleSave = async () => {
    if (!organization) return;

    setIsSaving(true);
    try {
      await organizationService.update(organization.id, editData);
      setOrganization({ ...organization, ...editData });
      setIsEditing(false);
      toast.success('Organization updated');
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error('Failed to update organization');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle primary admin change
  const handlePrimaryAdminChange = async (userId: string) => {
    if (!organization) return;

    setIsChangingPrimaryAdmin(true);
    try {
      if (userId === '') {
        // Clear primary admin
        await organizationService.clearPrimaryAdmin(organization.id);
        setOrganization({ ...organization, primaryAdminId: undefined, primaryAdminName: undefined });
        toast.success('Primary admin removed');
      } else {
        // Set new primary admin
        const selectedUser = users.find(u => u.uid === userId);
        if (selectedUser) {
          await organizationService.setPrimaryAdmin(organization.id, userId, selectedUser.name);
          setOrganization({ ...organization, primaryAdminId: userId, primaryAdminName: selectedUser.name });
          toast.success(`${selectedUser.name} set as primary admin`);
        }
      }
    } catch (error) {
      console.error('Error updating primary admin:', error);
      toast.error('Failed to update primary admin');
    } finally {
      setIsChangingPrimaryAdmin(false);
    }
  };

  // Access denied
  if (!isProviderAdmin) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-2">
            Only provider administrators can view organization details.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900">Organization not found</h2>
          <button
            onClick={() => navigate('/organizations')}
            className="mt-4 text-indigo-600 hover:text-indigo-700"
          >
            Back to organizations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/organizations')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Building2 className="h-7 w-7 mr-3 text-indigo-600" />
              {isEditing ? (
                <input
                  type="text"
                  value={editData.name || ''}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="border-b border-indigo-500 focus:outline-none px-1"
                />
              ) : (
                organization.name
              )}
            </h1>
            <p className="text-gray-500 mt-1">
              Created {organization.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditData({
                    name: organization.name,
                    contactName: organization.contactName,
                    contactEmail: organization.contactEmail,
                    contactPhone: organization.contactPhone,
                    accountingEmails: organization.accountingEmails,
                    address: organization.address,
                  });
                }}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-indigo-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.userCount || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <FolderOpen className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Projects</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.projectCount || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Active Workloads</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeWorkloadCount || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalLeadCount || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Admin Section */}
      <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg border border-amber-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-200 rounded-lg">
              <Crown className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Primary Administrator</h2>
              <p className="text-sm text-gray-600">
                Primary admin has full access to fiscal data, contracts, and billing information
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4">
          {organization.primaryAdminId ? (
            <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-amber-200">
              <div className="p-2 bg-amber-100 rounded-full">
                <Shield className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{organization.primaryAdminName || 'Unknown'}</p>
                <p className="text-sm text-gray-500">
                  {users.find(u => u.uid === organization.primaryAdminId)?.email || 'Primary Admin'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-white/50 rounded-lg px-4 py-3 border border-dashed border-amber-300">
              <Shield className="h-5 w-5 text-gray-400" />
              <p className="text-gray-500">No primary admin assigned</p>
            </div>
          )}

          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {organization.primaryAdminId ? 'Change Primary Admin' : 'Assign Primary Admin'}
            </label>
            <div className="relative">
              <select
                value={organization.primaryAdminId || ''}
                onChange={(e) => handlePrimaryAdminChange(e.target.value)}
                disabled={isChangingPrimaryAdmin || users.length === 0}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
              >
                <option value="">
                  {users.length === 0 ? 'No users available' : 'Select a user...'}
                </option>
                {users
                  .filter(u => u.role === 'client_admin' || u.role === 'client_user')
                  .map((user) => (
                    <option key={user.uid} value={user.uid}>
                      {user.name} ({user.role})
                    </option>
                  ))}
              </select>
              {isChangingPrimaryAdmin && (
                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                </div>
              )}
            </div>
            {users.filter(u => u.role === 'client_admin' || u.role === 'client_user').length === 0 && users.length > 0 && (
              <p className="mt-1 text-xs text-amber-600">
                Only client_admin or client_user roles can be primary admin
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>

          <div className="space-y-4">
            <div className="flex items-start">
              <User className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Contact Name</p>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.contactName || ''}
                    onChange={(e) => setEditData({ ...editData, contactName: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{organization.contactName || 'Not set'}</p>
                )}
              </div>
            </div>

            <div className="flex items-start">
              <Mail className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Email</p>
                {isEditing ? (
                  <input
                    type="email"
                    value={editData.contactEmail || ''}
                    onChange={(e) => setEditData({ ...editData, contactEmail: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{organization.contactEmail || 'Not set'}</p>
                )}
              </div>
            </div>

            <div className="flex items-start">
              <Mail className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Accounting / AR contact(s) <span className="text-gray-400">— CC'd on invoices</span></p>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.accountingEmails || ''}
                    onChange={(e) => setEditData({ ...editData, accountingEmails: e.target.value })}
                    placeholder="ap@client.org, billing@client.org"
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{organization.accountingEmails || 'Not set'}</p>
                )}
              </div>
            </div>

            <div className="flex items-start">
              <Phone className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Phone</p>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editData.contactPhone || ''}
                    onChange={(e) => setEditData({ ...editData, contactPhone: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                  />
                ) : (
                  <p className="text-gray-900">{organization.contactPhone || 'Not set'}</p>
                )}
              </div>
            </div>

            <div className="flex items-start">
              <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Address</p>
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Street"
                      value={editData.address?.street || ''}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          address: { ...editData.address!, street: e.target.value },
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="City"
                        value={editData.address?.city || ''}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            address: { ...editData.address!, city: e.target.value },
                          })
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={editData.address?.state || ''}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            address: { ...editData.address!, state: e.target.value },
                          })
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                ) : organization.address ? (
                  <div className="text-gray-900">
                    <p>{organization.address.street}</p>
                    <p>
                      {organization.address.city}, {organization.address.state}{' '}
                      {organization.address.zipCode}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-900">Not set</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Users */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Users ({users.length})</h2>

          {users.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No users in this organization</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {users.slice(0, 10).map((user) => (
                <div key={user.uid} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    {user.role}
                  </span>
                </div>
              ))}
              {users.length > 10 && (
                <p className="text-sm text-gray-500 text-center">
                  +{users.length - 10} more users
                </p>
              )}
            </div>
          )}
        </div>

        {/* Projects */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Projects ({projects.length})</h2>

          {projects.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No projects in this organization</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {projects.slice(0, 10).map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <div>
                    <p className="font-medium text-gray-900">{project.name}</p>
                    <p className="text-sm text-gray-500">
                      {project.assignedUsers?.length || 0} users
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      project.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {project.status}
                  </span>
                </div>
              ))}
              {projects.length > 10 && (
                <p className="text-sm text-gray-500 text-center">
                  +{projects.length - 10} more projects
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetailPage;
